import { usePageAccessChecker } from 'src/app/AppAuth/usePageAccess'
import React, { useState } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useReadAccessChecker } from 'src/app/AppAuth/useReadAccess'
import { GridProps, TypographyProps } from 'styled-system'
import { InfoButton } from 'src/components/PageLayout/InfoButton'
import BootstrapTab from 'react-bootstrap/Tab'
import { TabContainerProps } from 'react-bootstrap/TabContainer'
import { CountChip } from '../Chips/BasicChip'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../Tooltip/Tooltip'
import { TableListLoader } from '../ContentLoaders/TableListLoader'
import {
  TabContainer,
  TabRow,
  TabContent,
  TabNav,
  TabNote,
  TabTitle,
  TabNavItem,
} from './Tabs.styles'

export type TabType = {
  requiredPageCapability?: CAPABILITY
  requiredCapabilities?: CAPABILITY[]
  title: string
  toolTipBody?: string
  count?: number | JSX.Element
  body: JSX.Element
  note?: JSX.Element
  disabled?: boolean
  tabKey?: string | number
  unmountOnExit?: boolean
  // Delay mounting the pane until it's first activated.
  mountOnEnter?: boolean
}

type TabsProps = {
  tabs: TabType[]
  defaultActiveKey?: string | number
  activeKey?: string | number
  onSelect?: TabContainerProps['onSelect']
  tabTitleFontStyles?: TypographyProps
  gridStyles?: GridProps
  fillHeight?: boolean
}

export function Tabs({
  tabs,
  defaultActiveKey = '0',
  onSelect,
  tabTitleFontStyles,
  gridStyles,
  activeKey,
  fillHeight,
}: TabsProps): JSX.Element {
  const canRead = useReadAccessChecker()
  const canOpenPage = usePageAccessChecker()
  const [selectedKey, setSelectedKey] = useState(defaultActiveKey)
  // Assign fallback keys before filtering so URLs and callers using numeric
  // keys continue to identify the same pane when a related feature is hidden.
  const visibleTabs = tabs
    .map((tab, idx) => ({ ...tab, tabKey: tab.tabKey ?? idx }))
    .filter(
      (tab) =>
        (!tab.requiredPageCapability ||
          canOpenPage(tab.requiredPageCapability)) &&
        (tab.requiredCapabilities || []).every(canRead),
    )
  const requestedKey = activeKey ?? selectedKey
  const resolvedKey = visibleTabs.some(
    (tab) => String(tab.tabKey) === String(requestedKey),
  )
    ? requestedKey
    : visibleTabs[0]?.tabKey
  const innerTabs: JSX.Element[] = []
  const Panes: JSX.Element[] = []

  visibleTabs.forEach(
    (
      {
        title,
        toolTipBody,
        count,
        body,
        note,
        disabled,
        tabKey,
        unmountOnExit,
        mountOnEnter,
      },
      idx,
    ) => {
      const eventKey = tabKey !== undefined ? tabKey : idx
      innerTabs.push(
        <TabNavItem key={`${title}-${eventKey}`}>
          <TabTitle
            eventKey={eventKey}
            {...tabTitleFontStyles}
            data-testid={`TAB_IDX_${eventKey}`}
            disabled={disabled}
          >
            <span className="tab-title">{title}</span>
            {count !== undefined && <CountChip ml="xs">{count}</CountChip>}
            {toolTipBody && (
              <Tooltip
                id={`tab-tooltip-${title}`}
                placement={TooltipPositions.right}
                overlayType={TooltipOverlayType.popover}
                tooltipBody={toolTipBody}
              >
                <InfoButton as="div" ml="xxs" />
              </Tooltip>
            )}
          </TabTitle>
        </TabNavItem>,
      )

      Panes.push(
        <BootstrapTab.Pane
          key={eventKey}
          eventKey={eventKey}
          unmountOnExit={unmountOnExit}
          mountOnEnter={mountOnEnter}
        >
          {note && <TabNote>{note}</TabNote>}
          {body}
        </BootstrapTab.Pane>,
      )
    },
  )

  if (!visibleTabs.length) return <></>
  return (
    <BootstrapTab.Container
      activeKey={resolvedKey}
      onSelect={(key, event) => {
        if (key !== null) setSelectedKey(key)
        onSelect?.(key, event)
      }}
    >
      <TabContainer {...gridStyles} $fillHeight={fillHeight}>
        <TabRow $fillHeight={fillHeight}>
          <TabNav>{innerTabs}</TabNav>
        </TabRow>
        <TabRow $fillHeight={fillHeight} $content>
          <TabContent $fillHeight={fillHeight}>{Panes}</TabContent>
        </TabRow>
      </TabContainer>
    </BootstrapTab.Container>
  )
}

export const getLoadingTab = ({ title }: { title: string }): TabType => ({
  title,
  count: 0,
  body: <TableListLoader />,
})
